import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { formatPrice } from '../utils/formatPrice';
import { API_BASE_URL, getAuthHeaders } from '../services/api';
import DealSlipViewModal from '../components/DealSlipViewModal';
import DealConfirmationButton from '../components/DealConfirmationButton';
import BuySellLetterButtons from '../components/BuySellLetterButtons';
import BuybackApprovedLettersPanel from '../components/BuybackApprovedLettersPanel';
import { resolveBuybackSellerBuyer } from '../utils/buybackDeal';
import LimitExceededNotice from '../components/LimitExceededNotice';

export default function BackOfficeFinalBuybackBlotter() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [limitExceededInfo, setLimitExceededInfo] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [viewingTransaction, setViewingTransaction] = useState(null);
  // Approved deals (for the Confirmations section below the approval queue) -
  // a deal disappears from the Pending_Final_Approval queue the moment it's
  // approved, so confirmations need their own fetch against the Approved
  // status rather than trying to find them in the pending list.
  const [approvedTransactions, setApprovedTransactions] = useState([]);

  useEffect(() => {
    fetchTransactions();
    fetchApprovedTransactions();
  }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/buyback/status/Pending_Final_Approval`, { headers: getAuthHeaders() });
      if (response.data?.success) {
        setTransactions(response.data.data || []);
      } else {
        setTransactions([]);
      }
    } catch (err) {
      setError('Failed to fetch transactions');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovedTransactions = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/buyback/status/Approved`, { headers: getAuthHeaders() });
      setApprovedTransactions(response.data?.success ? (response.data.data || []) : []);
    } catch (err) {
      setApprovedTransactions([]);
    }
  };

  const handleApprove = async (id) => {
    setApprovingId(id);
    setError('');
    setLimitExceededInfo(null);
    try {
      await axios.patch(`${API_BASE_URL}/buyback/${id}/status`, {
        status: 'Approved',
        action: 'approve'
      }, { headers: getAuthHeaders() });
      // Remove the approved transaction from the list and refresh the
      // approved/confirmations section so it shows up there immediately.
      setTransactions(transactions.filter(tx => tx.id !== id));
      fetchApprovedTransactions();
    } catch (err) {
      if (err.response?.data?.limitExceeded) {
        setLimitExceededInfo(err.response.data);
      } else {
        setError('Failed to approve transaction');
      }
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (id) => {
    setApprovingId(id);
    setError('');
    try {
      await axios.patch(`${API_BASE_URL}/buyback/${id}/status`, {
        status: 'Rejected',
        action: 'reject'
      }, { headers: getAuthHeaders() });
      // Remove the rejected transaction from the list
      setTransactions(transactions.filter(tx => tx.id !== id));
    } catch (err) {
      setError('Failed to reject transaction');
    } finally {
      setApprovingId(null);
    }
  };

  return (
    <div className="sister-blotter">
      <h2 style={{ fontSize: '2rem', marginBottom: '16px' }}>Back Office Final Buyback Blotter</h2>
      <p style={{ color: '#666', marginBottom: '24px' }}>Final review and approval of buyback deals that have been verified by back office</p>
      
      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red', marginBottom: 16 }}>{error}</div>}
      <LimitExceededNotice info={limitExceededInfo} onDismiss={() => setLimitExceededInfo(null)} />
      {!loading && transactions.length === 0 && <div>No buyback deals pending final approval.</div>}
      
      {!loading && transactions.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
          <thead style={{ background: '#f1f3f7' }}>
            <tr>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>Deal Number</th>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>Trade Date</th>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>ISIN</th>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>Seller</th>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>Buyer</th>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>Face Value</th>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>Leg 1 Settlement</th>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>Leg 2 Settlement</th>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>Interest Rate</th>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>Front Office Verifier</th>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>Back Office Verifier</th>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>Status</th>
              <th style={{ padding: '8px', border: '1px solid #eee' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map(tx => {
              const { seller, buyer } = resolveBuybackSellerBuyer(tx);
              return (
              <tr key={tx.id}>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  <strong style={{ color: '#007bff' }}>{tx.deal_number}</strong>
                </td>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  {tx.leg1_trade_date ? format(new Date(tx.leg1_trade_date), 'yyyy-MM-dd') : ''}
                </td>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                    {tx.leg1_isin}
                  </span>
                </td>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  {seller}
                </td>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  {buyer}
                </td>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  {formatPrice(tx.leg1_adjusted_face_value ?? tx.leg1_face_value)}
                </td>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  {formatPrice(tx.leg1_settlement_amount)}
                </td>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  {formatPrice(tx.leg2_settlement_amount)}
                </td>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  <span style={{ fontWeight: 'bold', color: '#28a745' }}>
                    {parseFloat(tx.leg1_interest_rate || 0).toFixed(2)}%
                  </span>
                </td>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  <span style={{ fontSize: '0.85em', color: '#666' }}>
                    {tx.verified_by_name || 'Unknown'}
                  </span>
                </td>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  <span style={{ fontSize: '0.85em', color: '#666' }}>
                    {tx.approved_by_name || 'Pending'}
                  </span>
                </td>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  <span 
                    className={`badge ${
                      tx.deal_status === 'Approved' ? 'badge-success' :
                      tx.deal_status === 'Pending_Final_Approval' ? 'badge-warning' :
                      tx.deal_status === 'Verified' ? 'badge-info' :
                      tx.deal_status === 'Pending_Verification' ? 'badge-warning' :
                      tx.deal_status === 'Rejected' ? 'badge-danger' :
                      'badge-secondary'
                    }`}
                  >
                    {tx.deal_status?.replace(/_/g, ' ') || 'Pending'}
                  </span>
                </td>
                <td style={{ padding: '8px', border: '1px solid #eee' }}>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button 
                      onClick={() => setViewingTransaction(tx)} 
                      style={{ 
                        padding: '6px 14px', 
                        background: '#007bff', 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: 4, 
                        cursor: 'pointer',
                        fontSize: '0.85em'
                      }}
                    >
                      View
                    </button>
                    {tx.deal_status === 'Approved' && (
                      <DealConfirmationButton
                        dealType="buyback"
                        dealId={tx.id}
                        refNumberHint={tx.deal_number}
                        className=""
                        style={{ padding: '6px 14px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.85em' }}
                      />
                    )}
                    <BuySellLetterButtons tx={tx} />
                    <button
                      onClick={() => handleApprove(tx.id)} 
                      disabled={approvingId === tx.id}
                      style={{ 
                        padding: '6px 14px', 
                        background: '#28a745', 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: 4, 
                        cursor: 'pointer',
                        fontSize: '0.85em'
                      }}
                    >
                      {approvingId === tx.id ? 'Approving...' : 'Final Approve'}
                    </button>
                    <button 
                      onClick={() => handleReject(tx.id)} 
                      disabled={approvingId === tx.id}
                      style={{ 
                        padding: '6px 14px', 
                        background: '#dc3545', 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: 4, 
                        cursor: 'pointer',
                        fontSize: '0.85em'
                      }}
                    >
                      {approvingId === tx.id ? 'Rejecting...' : 'Reject'}
                    </button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}
      
      <div style={{ marginTop: '24px', textAlign: 'center' }}>
        <button 
          onClick={fetchTransactions}
          disabled={loading}
          style={{ 
            padding: '12px 24px', 
            background: '#007bff', 
            color: '#fff', 
            border: 'none', 
            borderRadius: 4, 
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <BuybackApprovedLettersPanel
        transactions={approvedTransactions}
        onView={setViewingTransaction}
      />

      {viewingTransaction && (
        <DealSlipViewModal
          transaction={viewingTransaction}
          onClose={() => setViewingTransaction(null)}
          dealType="Buyback"
        />
      )}
    </div>
  );
}
